import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Users, Search, Edit2, Trash2, Building2, Phone, Mail, 
  Filter, RefreshCw, UserCheck, UserPlus, ChevronDown, ChevronRight,
  Eye, EyeOff, Save, X, FileText, Calendar, Clock,
  PauseCircle, PlayCircle, XCircle, AlertTriangle, CheckCircle2,
  Briefcase, MessageSquare, Download, ExternalLink, Key, User
} from 'lucide-react';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const MasterClientes = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [prospectos, setProspectos] = useState([]);
  const [clientesActivos, setClientesActivos] = useState([]);
  const [clientesSuspendidos, setClientesSuspendidos] = useState([]);
  const [clientesCerrados, setClientesCerrados] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [origenFilter, setOrigenFilter] = useState('all');
  const [currentUser, setCurrentUser] = useState(null);
  
  // Estados para filas expandidas
  const [expandedRows, setExpandedRows] = useState({});
  const [clientDocuments, setClientDocuments] = useState({});
  const [loadingDocs, setLoadingDocs] = useState({});
  
  // Estado para edición
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '', email: '', phone: '', company: '',
    origen: '', rut: '', rubro: '', clave_sii: '', clave_unica: ''
  });
  const [showClaveSii, setShowClaveSii] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Estado para visibilidad de contraseñas en fila expandida
  const [visiblePasswords, setVisiblePasswords] = useState({});
  
  // Estados para suspensión
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendMotivo, setSuspendMotivo] = useState('');
  const [suspending, setSuspending] = useState(false);
  
  // Estados para cierre
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeForm, setCloseForm] = useState({ motivo: '', feedback: '' });
  const [closing, setClosing] = useState(false);
  
  // Toggle visibilidad de contraseñas
  const togglePasswordVisibility = (key) => {
    setVisiblePasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try { setCurrentUser(JSON.parse(storedUser)); } catch { setCurrentUser(null); }
    }
  }, []);

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await api.get('/clients');
      const allClients = response.data;
      
      // Separar por estado y tipo
      setProspectos(allClients.filter(c => c.is_prospect === true || c.is_prospect === undefined));
      setClientesActivos(allClients.filter(c => c.is_prospect === false && c.estado !== 'suspendido' && c.estado !== 'cerrado'));
      setClientesSuspendidos(allClients.filter(c => c.estado === 'suspendido'));
      setClientesCerrados(allClients.filter(c => c.estado === 'cerrado'));
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const hasAccess = () => {
    if (!currentUser) return false;
    if (currentUser.role === 'Administrador') return true;
    if (currentUser.role === 'Líder' && currentUser.areas?.includes('Comercial')) return true;
    return false;
  };

  // Expandir/colapsar fila y cargar documentos
  const toggleExpand = async (clientId) => {
    const isExpanded = expandedRows[clientId];
    
    if (!isExpanded && !clientDocuments[clientId]) {
      // Cargar documentos si no están cargados
      setLoadingDocs(prev => ({ ...prev, [clientId]: true }));
      try {
        const response = await api.get(`/clients/${clientId}/documentos`);
        setClientDocuments(prev => ({ ...prev, [clientId]: response.data }));
      } catch (error) {
        toast.error('Error al cargar documentos');
      } finally {
        setLoadingDocs(prev => ({ ...prev, [clientId]: false }));
      }
    }
    
    setExpandedRows(prev => ({ ...prev, [clientId]: !isExpanded }));
  };

  const handleOpenEdit = (client) => {
    setSelectedClient(client);
    setEditForm({
      name: client.name || '', email: client.email || '',
      phone: client.phone || '', company: client.company || '',
      origen: client.origen || '', rut: client.rut || '',
      rubro: client.rubro || '', clave_sii: client.clave_sii || ''
    });
    setShowClaveSii(false);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedClient) return;
    setSaving(true);
    try {
      await api.put(`/clients/${selectedClient.id}`, editForm);
      toast.success('Datos actualizados');
      setShowEditModal(false);
      fetchClients();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clientId, name) => {
    if (!confirm(`¿Eliminar "${name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Registro eliminado');
      fetchClients();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleConvertToClient = async (clientId) => {
    try {
      await api.put(`/clients/${clientId}`, { is_prospect: false, estado: 'activo' });
      toast.success('Prospecto convertido a cliente activo');
      fetchClients();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  // Suspender cliente
  const handleOpenSuspend = (client) => {
    setSelectedClient(client);
    setSuspendMotivo('');
    setShowSuspendModal(true);
  };

  const handleSuspend = async () => {
    if (!selectedClient || !suspendMotivo.trim()) {
      toast.error('Debe indicar el motivo de suspensión');
      return;
    }
    setSuspending(true);
    try {
      await api.put(`/clients/${selectedClient.id}/suspender`, { motivo: suspendMotivo });
      toast.success('Cliente suspendido. Tareas bloqueadas.');
      setShowSuspendModal(false);
      fetchClients();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSuspending(false);
    }
  };

  // Reactivar cliente
  const handleReactivate = async (clientId) => {
    try {
      await api.put(`/clients/${clientId}/reactivar`);
      toast.success('Cliente reactivado. Tareas desbloqueadas.');
      fetchClients();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  // Cerrar cliente
  const handleOpenClose = (client) => {
    setSelectedClient(client);
    setCloseForm({ motivo: '', feedback: '' });
    setShowCloseModal(true);
  };

  const handleClose = async () => {
    if (!selectedClient) return;
    if (!closeForm.motivo.trim() || !closeForm.feedback.trim()) {
      toast.error('Complete todos los campos');
      return;
    }
    if (closeForm.feedback.length < 20) {
      toast.error('El feedback debe tener al menos 20 caracteres');
      return;
    }
    setClosing(true);
    try {
      await api.put(`/clients/${selectedClient.id}/cerrar`, closeForm);
      toast.success('Cliente cerrado exitosamente');
      setShowCloseModal(false);
      fetchClients();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setClosing(false);
    }
  };

  // Filtrar clientes
  const filterClients = (clients) => {
    return clients.filter(c => {
      const matchesSearch = 
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.rut?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesOrigen = origenFilter === 'all' || c.origen === origenFilter;
      return matchesSearch && matchesOrigen;
    });
  };

  const getOrigenBadgeColor = (origen) => {
    switch (origen) {
      case 'Meta': return 'bg-blue-100 text-blue-700';
      case 'Google Ads': return 'bg-red-100 text-red-700';
      case 'Referenciado': return 'bg-green-100 text-green-700';
      case 'Nuevo Servicio': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Borrador': return <Badge variant="outline" className="text-slate-600">Borrador</Badge>;
      case 'Ejecución/Activa': 
      case 'Ejecucion': return <Badge className="bg-green-100 text-green-700">Activa</Badge>;
      case 'Aceptada': return <Badge className="bg-emerald-100 text-emerald-700">Aceptada</Badge>;
      case 'Terminada': return <Badge className="bg-blue-100 text-blue-700">Terminada</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Componente de fila expandible
  const ClientRow = ({ client, type }) => {
    const isExpanded = expandedRows[client.id];
    const docs = clientDocuments[client.id];
    const isLoadingDocs = loadingDocs[client.id];

    return (
      <>
        <tr className="border-b hover:bg-slate-50 transition-colors">
          <td className="py-3 px-4">
            <button
              onClick={() => toggleExpand(client.id)}
              className="flex items-center gap-2 text-left w-full"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-400" />
              )}
              <div>
                <div className="font-medium text-slate-900">{client.name}</div>
                <div className="text-xs text-slate-500">
                  {client.created_at ? new Date(client.created_at).toLocaleDateString('es-CL') : '-'}
                </div>
              </div>
            </button>
          </td>
          <td className="py-3 px-4">
            {client.origen ? (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getOrigenBadgeColor(client.origen)}`}>
                {client.origen}
              </span>
            ) : <span className="text-slate-400">-</span>}
          </td>
          <td className="py-3 px-4">
            <div className="flex items-center gap-2">
              {client.company && <Building2 className="w-4 h-4 text-slate-400" />}
              <span className="text-slate-700">{client.company || '-'}</span>
            </div>
          </td>
          <td className="py-3 px-4">
            <span className="font-mono text-sm text-slate-600">{client.rut || '-'}</span>
          </td>
          <td className="py-3 px-4">
            <span className="text-slate-600">{client.rubro || '-'}</span>
          </td>
          <td className="py-3 px-4">
            <div className="space-y-1">
              {client.email && (
                <div className="flex items-center gap-1 text-xs text-slate-600">
                  <Mail className="w-3 h-3" />{client.email}
                </div>
              )}
              {client.phone && (
                <div className="flex items-center gap-1 text-xs text-slate-600">
                  <Phone className="w-3 h-3" />{client.phone}
                </div>
              )}
              {!client.email && !client.phone && <span className="text-slate-400 text-xs">Sin contacto</span>}
            </div>
          </td>
          <td className="py-3 px-4">
            <div className="flex items-center justify-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(client)} title="Editar">
                <Edit2 className="w-4 h-4" />
              </Button>
              
              {type === 'prospecto' && (
                <Button
                  variant="ghost" size="sm"
                  onClick={() => handleConvertToClient(client.id)}
                  title="Convertir a Cliente"
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                >
                  <UserCheck className="w-4 h-4" />
                </Button>
              )}
              
              {type === 'activo' && (
                <>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => handleOpenSuspend(client)}
                    title="Suspender"
                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                  >
                    <PauseCircle className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => handleOpenClose(client)}
                    title="Cerrar Cliente"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </>
              )}
              
              {type === 'suspendido' && (
                <Button
                  variant="ghost" size="sm"
                  onClick={() => handleReactivate(client.id)}
                  title="Reactivar"
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                >
                  <PlayCircle className="w-4 h-4" />
                </Button>
              )}
              
              <Button
                variant="ghost" size="sm"
                onClick={() => handleDelete(client.id, client.name)}
                title="Eliminar"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </td>
        </tr>
        
        {/* Fila expandida con documentos */}
        <AnimatePresence>
          {isExpanded && (
            <tr>
              <td colSpan={7} className="bg-slate-50 p-0">
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 border-t border-slate-200">
                    {isLoadingDocs ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                        <span className="ml-2 text-slate-500">Cargando documentos...</span>
                      </div>
                    ) : docs ? (
                      <div className="space-y-6">
                        {/* Empresas Asociadas */}
                        {(client.empresas?.length > 0 || client.company) && (
                          <div>
                            <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                              <Building2 className="w-4 h-4" />
                              Empresas Asociadas
                              <Badge variant="outline">
                                {client.empresas?.length || (client.company ? 1 : 0)}
                              </Badge>
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {client.empresas?.length > 0 ? (
                                client.empresas.map((emp, idx) => (
                                  <Card key={emp.id || idx} className="p-3 bg-white">
                                    <div className="font-medium text-sm text-slate-900">{emp.nombre}</div>
                                    <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                                      {emp.rut && <div>RUT: {emp.rut}</div>}
                                      {emp.rubro && <div>Rubro: {emp.rubro}</div>}
                                    </div>
                                  </Card>
                                ))
                              ) : client.company && (
                                <Card className="p-3 bg-white">
                                  <div className="font-medium text-sm text-slate-900">{client.company}</div>
                                  <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                                    {client.rut && <div>RUT: {client.rut}</div>}
                                    {client.rubro && <div>Rubro: {client.rubro}</div>}
                                  </div>
                                </Card>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* PSPs */}
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <Briefcase className="w-4 h-4" />
                            Propuestas de Servicio (PSP)
                            <Badge variant="outline">{docs.proposals?.length || 0}</Badge>
                          </h4>
                          {docs.proposals?.length > 0 ? (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {docs.proposals.map(psp => (
                                <Card key={psp.id} className="p-3 bg-white">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm">{psp.folio}</span>
                                        {getStatusBadge(psp.status)}
                                      </div>
                                      <div className="text-xs text-slate-500 mt-1">
                                        {psp.servicios?.map(s => s.nombre).join(', ') || 'Sin servicios'}
                                      </div>
                                      <div className="text-xs text-slate-400 mt-1">
                                        <Calendar className="w-3 h-3 inline mr-1" />
                                        {psp.created_at ? new Date(psp.created_at).toLocaleDateString('es-CL') : '-'}
                                      </div>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button variant="ghost" size="sm" title="Ver PDF">
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                      <Button variant="ghost" size="sm" title="Descargar">
                                        <Download className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500 italic">Sin propuestas</p>
                          )}
                        </div>
                        
                        {/* Minutas */}
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Minutas de Reunión
                            <Badge variant="outline">{docs.meetings?.length || 0}</Badge>
                          </h4>
                          {docs.meetings?.length > 0 ? (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {docs.meetings.map(meeting => (
                                <Card key={meeting.id} className="p-3 bg-white">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="font-medium text-sm">{meeting.title}</div>
                                      <div className="text-xs text-slate-500 mt-1">
                                        {meeting.ai_analysis?.tareas?.length || 0} tareas extraídas
                                      </div>
                                      <div className="text-xs text-slate-400 mt-1">
                                        <Clock className="w-3 h-3 inline mr-1" />
                                        {meeting.created_at ? new Date(meeting.created_at).toLocaleDateString('es-CL') : '-'}
                                        {meeting.transferido_operaciones && (
                                          <Badge className="ml-2 bg-green-100 text-green-700 text-xs">Transferida</Badge>
                                        )}
                                      </div>
                                    </div>
                                    <Button variant="ghost" size="sm" title="Ver detalle">
                                      <ExternalLink className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500 italic">Sin minutas</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Sección de Credenciales Jerárquicas */}
                      <div className="mt-6 pt-4 border-t border-slate-200">
                        <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Key className="w-4 h-4" />
                          Credenciales
                          <Badge variant="outline">
                            {1 + (client.empresas?.length || 0)} {(client.empresas?.length || 0) === 0 ? 'entidad' : 'entidades'}
                          </Badge>
                        </h4>
                        
                        <div className="space-y-3">
                          {/* Credenciales del Prospecto/Persona */}
                          <Card className="p-3 bg-white border-l-4 border-l-[#1E3649]">
                            <div className="flex items-center gap-2 mb-2">
                              <User className="w-4 h-4 text-[#1E3649]" />
                              <span className="font-medium text-sm text-[#1E3649]">{client.name}</span>
                              <Badge className="text-xs bg-slate-100 text-slate-600">Persona</Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-3 text-xs">
                              <div>
                                <span className="text-slate-500">RUT:</span>
                                <span className="ml-2 font-medium">{client.rut || '-'}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-slate-500">Clave SII:</span>
                                <span className="ml-1 font-mono">
                                  {client.clave_sii ? (visiblePasswords[`${client.id}_sii`] ? client.clave_sii : '••••••••') : '-'}
                                </span>
                                {client.clave_sii && (
                                  <button onClick={() => togglePasswordVisibility(`${client.id}_sii`)} className="text-slate-400 hover:text-slate-600">
                                    {visiblePasswords[`${client.id}_sii`] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                  </button>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-slate-500">Clave Única:</span>
                                <span className="ml-1 font-mono">
                                  {client.clave_unica ? (visiblePasswords[`${client.id}_unica`] ? client.clave_unica : '••••••••') : '-'}
                                </span>
                                {client.clave_unica && (
                                  <button onClick={() => togglePasswordVisibility(`${client.id}_unica`)} className="text-slate-400 hover:text-slate-600">
                                    {visiblePasswords[`${client.id}_unica`] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                  </button>
                                )}
                              </div>
                            </div>
                          </Card>
                          
                          {/* Credenciales de cada Empresa */}
                          {client.empresas?.map((emp, idx) => (
                            <Card key={emp.id || idx} className="p-3 bg-white border-l-4 border-l-blue-500">
                              <div className="flex items-center gap-2 mb-2">
                                <Building2 className="w-4 h-4 text-blue-500" />
                                <span className="font-medium text-sm text-slate-700">{emp.nombre}</span>
                                <Badge className="text-xs bg-blue-100 text-blue-600">Empresa</Badge>
                              </div>
                              <div className="grid grid-cols-3 gap-3 text-xs">
                                <div>
                                  <span className="text-slate-500">RUT:</span>
                                  <span className="ml-2 font-medium">{emp.rut || '-'}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-slate-500">Clave SII:</span>
                                  <span className="ml-1 font-mono">
                                    {emp.clave_sii ? (visiblePasswords[`${client.id}_emp${idx}_sii`] ? emp.clave_sii : '••••••••') : '-'}
                                  </span>
                                  {emp.clave_sii && (
                                    <button onClick={() => togglePasswordVisibility(`${client.id}_emp${idx}_sii`)} className="text-slate-400 hover:text-slate-600">
                                      {visiblePasswords[`${client.id}_emp${idx}_sii`] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                    </button>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-slate-500">Clave Única:</span>
                                  <span className="ml-1 font-mono">
                                    {emp.clave_unica ? (visiblePasswords[`${client.id}_emp${idx}_unica`] ? emp.clave_unica : '••••••••') : '-'}
                                  </span>
                                  {emp.clave_unica && (
                                    <button onClick={() => togglePasswordVisibility(`${client.id}_emp${idx}_unica`)} className="text-slate-400 hover:text-slate-600">
                                      {visiblePasswords[`${client.id}_emp${idx}_unica`] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">Error al cargar documentos</p>
                    )}
                    
                    {/* Info de suspensión o cierre */}
                    {client.estado === 'suspendido' && client.motivo_suspension && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-2 text-amber-700 font-medium">
                          <AlertTriangle className="w-4 h-4" />
                          Cliente Suspendido
                        </div>
                        <p className="text-sm text-amber-600 mt-1">
                          <strong>Motivo:</strong> {client.motivo_suspension}
                        </p>
                        <p className="text-xs text-amber-500 mt-1">
                          Fecha: {client.fecha_suspension ? new Date(client.fecha_suspension).toLocaleDateString('es-CL') : '-'}
                        </p>
                      </div>
                    )}
                    
                    {client.estado === 'cerrado' && (
                      <div className="mt-4 p-3 bg-slate-100 border border-slate-200 rounded-lg">
                        <div className="flex items-center gap-2 text-slate-700 font-medium">
                          <XCircle className="w-4 h-4" />
                          Relación Cerrada
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          <strong>Motivo:</strong> {client.motivo_cierre || '-'}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">
                          <strong>Feedback:</strong> {client.feedback_cierre || '-'}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Fecha: {client.fecha_cierre ? new Date(client.fecha_cierre).toLocaleDateString('es-CL') : '-'}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              </td>
            </tr>
          )}
        </AnimatePresence>
      </>
    );
  };

  // Tabla reutilizable
  const ClientTable = ({ clients, type, emptyMessage }) => (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#1E3649] text-white">
            <tr>
              <th className="text-left py-3 px-4 font-medium">
                {type === 'prospecto' ? 'Prospecto' : 'Cliente'}
              </th>
              <th className="text-left py-3 px-4 font-medium">Origen</th>
              <th className="text-left py-3 px-4 font-medium">Empresa</th>
              <th className="text-left py-3 px-4 font-medium">RUT</th>
              <th className="text-left py-3 px-4 font-medium">Rubro</th>
              <th className="text-left py-3 px-4 font-medium">Contacto</th>
              <th className="text-center py-3 px-4 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-slate-500">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Cargando...
                </td>
              </tr>
            ) : clients.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-slate-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              clients.map(client => (
                <ClientRow key={client.id} client={client} type={type} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );

  if (!hasAccess()) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Header />
        <div className="flex flex-1">
          <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
          <div className="flex-1 flex items-center justify-center">
            <Card className="p-8 text-center max-w-md">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-700 mb-2">Acceso Restringido</h2>
              <p className="text-slate-500">
                Este módulo está disponible únicamente para Administradores y Líderes Comerciales.
              </p>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />
      <div className="flex flex-1">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

        <div className="flex-1 overflow-y-auto max-h-screen" style={{ transform: 'scale(0.95)', transformOrigin: 'top left', width: '105.26%' }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-[#1E3649] rounded-xl">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Máster de Clientes</h1>
                  <p className="text-slate-500 text-sm">Ciclo de vida completo de prospectos y clientes</p>
                </div>
              </div>
              <Button onClick={fetchClients} variant="outline" className="gap-2">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>

            {/* Filtros */}
            <Card className="p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[250px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar por nombre, empresa o RUT..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <Select value={origenFilter} onValueChange={setOrigenFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filtrar por origen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los orígenes</SelectItem>
                      <SelectItem value="Meta">Meta</SelectItem>
                      <SelectItem value="Google Ads">Google Ads</SelectItem>
                      <SelectItem value="Referido">Referido</SelectItem>
                      <SelectItem value="Nuevo Servicio">Nuevo Servicio</SelectItem>
                      <SelectItem value="Nuevo Cliente">Nuevo Cliente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>

            {/* Tabs */}
            <Tabs defaultValue="prospectos" className="space-y-6">
              <TabsList className="bg-white border">
                <TabsTrigger value="prospectos" className="gap-2 data-[state=active]:bg-[#1E3649] data-[state=active]:text-white">
                  <UserPlus className="w-4 h-4" />
                  Prospectos
                  <Badge variant="secondary" className="ml-1">{filterClients(prospectos).length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="activos" className="gap-2 data-[state=active]:bg-[#1E3649] data-[state=active]:text-white">
                  <UserCheck className="w-4 h-4" />
                  Clientes Activos
                  <Badge variant="secondary" className="ml-1 bg-green-100 text-green-700">{filterClients(clientesActivos).length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="suspendidos" className="gap-2 data-[state=active]:bg-[#1E3649] data-[state=active]:text-white">
                  <PauseCircle className="w-4 h-4" />
                  Suspendidos
                  <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-700">{filterClients(clientesSuspendidos).length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="cerrados" className="gap-2 data-[state=active]:bg-[#1E3649] data-[state=active]:text-white">
                  <XCircle className="w-4 h-4" />
                  Cerrados
                  <Badge variant="secondary" className="ml-1 bg-slate-100 text-slate-600">{filterClients(clientesCerrados).length}</Badge>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="prospectos">
                <ClientTable 
                  clients={filterClients(prospectos)} 
                  type="prospecto"
                  emptyMessage="No hay prospectos registrados"
                />
              </TabsContent>

              <TabsContent value="activos">
                <ClientTable 
                  clients={filterClients(clientesActivos)} 
                  type="activo"
                  emptyMessage="No hay clientes activos"
                />
              </TabsContent>

              <TabsContent value="suspendidos">
                <ClientTable 
                  clients={filterClients(clientesSuspendidos)} 
                  type="suspendido"
                  emptyMessage="No hay clientes suspendidos"
                />
              </TabsContent>

              <TabsContent value="cerrados">
                <ClientTable 
                  clients={filterClients(clientesCerrados)} 
                  type="cerrado"
                  emptyMessage="No hay clientes cerrados"
                />
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </div>

      {/* Modal de Edición */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-4xl w-[90vw]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#1E3649]">
              Editar {selectedClient?.is_prospect !== false ? 'Prospecto' : 'Cliente'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
            <div className="space-y-4">
              <div>
                <Label>Nombre *</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Origen</Label>
                <Select value={editForm.origen || 'none'} onValueChange={(v) => setEditForm({...editForm, origen: v === 'none' ? '' : v})}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccionar origen" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin origen</SelectItem>
                    <SelectItem value="Meta">Meta</SelectItem>
                    <SelectItem value="Google Ads">Google Ads</SelectItem>
                    <SelectItem value="Referido">Referido</SelectItem>
                    <SelectItem value="Nuevo Servicio">Nuevo Servicio</SelectItem>
                    <SelectItem value="Nuevo Cliente">Nuevo Cliente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Razón Social / Empresa</Label>
                <Input value={editForm.company} onChange={(e) => setEditForm({...editForm, company: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>RUT</Label>
                <Input value={editForm.rut} onChange={(e) => setEditForm({...editForm, rut: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Rubro</Label>
                <Input value={editForm.rubro} onChange={(e) => setEditForm({...editForm, rubro: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Clave SII</Label>
                <div className="relative mt-1">
                  <Input type={showClaveSii ? 'text' : 'password'} value={editForm.clave_sii} onChange={(e) => setEditForm({...editForm, clave_sii: e.target.value})} />
                  <button type="button" onClick={() => setShowClaveSii(!showClaveSii)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showClaveSii ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancelar</Button>
            <Button onClick={handleSaveEdit} disabled={saving || !editForm.name} className="bg-[#1E3649] hover:bg-[#2A4A5F] text-white">
              {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : <><Save className="w-4 h-4 mr-2" />Guardar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Suspensión */}
      <Dialog open={showSuspendModal} onOpenChange={setShowSuspendModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-amber-600 flex items-center gap-2">
              <PauseCircle className="w-5 h-5" />
              Suspender Cliente
            </DialogTitle>
            <DialogDescription>
              Al suspender este cliente, todas sus tareas activas serán bloqueadas.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <Label className="text-slate-700 font-medium">Motivo de Suspensión *</Label>
            <Textarea
              value={suspendMotivo}
              onChange={(e) => setSuspendMotivo(e.target.value)}
              placeholder="Indique el motivo de la suspensión..."
              rows={3}
              className="mt-2"
            />
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowSuspendModal(false)}>Cancelar</Button>
            <Button onClick={handleSuspend} disabled={suspending || !suspendMotivo.trim()} className="bg-amber-600 hover:bg-amber-700 text-white">
              {suspending ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Suspendiendo...</> : 'Confirmar Suspensión'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Cierre */}
      <Dialog open={showCloseModal} onOpenChange={setShowCloseModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-red-600 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Cerrar Relación con Cliente
            </DialogTitle>
            <DialogDescription>
              Esta acción registrará el cierre de la relación comercial. Requiere un feedback detallado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-700 font-medium">Motivo de Cierre *</Label>
              <Select value={closeForm.motivo} onValueChange={(v) => setCloseForm({...closeForm, motivo: v})}>
                <SelectTrigger className="mt-2"><SelectValue placeholder="Seleccionar motivo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Terminación de PSP">Terminación de PSP</SelectItem>
                  <SelectItem value="Cambio de proveedor">Cambio de proveedor</SelectItem>
                  <SelectItem value="Cierre de empresa">Cierre de empresa del cliente</SelectItem>
                  <SelectItem value="Insatisfacción del cliente">Insatisfacción del cliente</SelectItem>
                  <SelectItem value="Morosidad">Morosidad prolongada</SelectItem>
                  <SelectItem value="Mutuo acuerdo">Mutuo acuerdo</SelectItem>
                  <SelectItem value="Otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Feedback Detallado * (mínimo 20 caracteres)</Label>
              <Textarea
                value={closeForm.feedback}
                onChange={(e) => setCloseForm({...closeForm, feedback: e.target.value})}
                placeholder="Describa detalladamente el resultado de la gestión, aprendizajes y cualquier información relevante para auditoría..."
                rows={5}
                className="mt-2"
              />
              <p className="text-xs text-slate-500 mt-1">
                {closeForm.feedback.length}/20 caracteres mínimos
              </p>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCloseModal(false)}>Cancelar</Button>
            <Button 
              onClick={handleClose} 
              disabled={closing || !closeForm.motivo || closeForm.feedback.length < 20} 
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {closing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Cerrando...</> : 'Confirmar Cierre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterClientes;
